<template>
  <section :aria-label="`Import history for ${cityName}`" :aria-busy="loading">
    <h5>Import history · {{ cityName }}</h5>
    <p class="small text-muted">
      Saved extract runs, newest first. Completed extracts are skipped when checked again;
      recorded repeat checks appear on the original run. File versions and dates help identify changes even when filenames stay the same.
    </p>
    <div v-if="error" class="alert alert-danger" role="alert">
      {{ error }} <button class="btn btn-sm btn-outline-danger ms-2" @click="loadHistory(failedAfter)">Retry</button>
    </div>
    <div v-if="imports.length" class="table-responsive">
      <table class="table table-sm align-top mb-2">
        <caption>Times use your local time zone. — means information was not recorded. Repeat counts include only recorded checks.</caption>
        <thead>
          <tr>
            <th scope="col">Import run</th>
            <th scope="col">Extract file</th>
            <th scope="col" class="text-end">Discovered</th>
            <th scope="col" class="text-end">Updated</th>
            <th scope="col" class="text-end">New</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="run in imports" :key="run.importId">
            <td>
              <span class="badge" :class="statusClass(run.status)">{{ statusLabel(run.status) }}</span>
              <div class="mt-1">
                <time v-if="run.completedAt || run.startedAt" :datetime="run.completedAt || run.startedAt">
                  {{ displayDate(run.completedAt || run.startedAt) }}
                </time>
                <span v-else class="text-muted">Time not recorded</span>
              </div>
              <div class="small text-muted">{{ sourceNames(run.sources) }}</div>
              <div v-if="run.status === 'completed' && run.runStatus === 'failed'" class="small text-muted mt-1">
                This municipality completed; the overall extract run failed.
              </div>
              <div v-if="run.absence" class="small mt-2">
                <strong>Absence check: {{ statusLabel(run.absence.status) }}</strong>
                <div v-if="run.absence.reason">{{ absenceReason(run.absence.reason) }}</div>
                <div v-if="run.absence.missing !== null && run.absence.missing !== undefined">
                  Missing: {{ displayCount(run.absence.missing) }} ·
                  Deactivated: {{ displayCount(run.absence.deactivated) }} ·
                  Review: {{ displayCount(run.absence.review) }}
                </div>
                <div v-for="scope in run.absence.sources || []" :key="scope.source">
                  <template v-if="scope.status === 'skipped'">{{ sourceNames([scope.source]) }}: {{ absenceReason(scope.reason) }}</template>
                </div>
              </div>
            </td>
            <td class="extract-details">
              <strong class="text-break">{{ run.extract.fileName || 'Filename not recorded' }}</strong>
              <div class="small">Data as of: {{ run.extract.dataAsOf || '—' }}</div>
              <div class="small">Exported: {{ displayDate(run.extract.exportedAt) }}</div>
              <div class="small">File modified: {{ displayDate(run.extract.lastModified) }}</div>
              <div v-if="run.duplicateCheckCount > 0" class="alert alert-warning small py-2 my-2">
                <strong>Same extract checked again</strong><br />
                Skipped {{ displayCount(run.duplicateCheckCount) }} repeat {{ run.duplicateCheckCount === 1 ? 'check' : 'checks' }}.
                Latest: {{ displayDate(run.lastDuplicateCheckAt) }}.
              </div>
              <details class="small mt-1">
                <summary>File and run details</summary>
                <dl class="mb-0 mt-2">
                  <dt>Object key</dt><dd class="text-break">{{ run.extract.objectKey || '—' }}</dd>
                  <dt>File version (ETag)</dt><dd class="text-break font-monospace">{{ run.extract.etag || '—' }}</dd>
                  <dt>Content hash (SHA-256)</dt><dd class="text-break font-monospace">{{ run.extract.contentHash || '—' }}</dd>
                  <dt>File size (bytes)</dt><dd>{{ displayCount(run.extract.contentLength) }}</dd>
                  <dt>Import ID</dt><dd class="text-break font-monospace">{{ run.importId }}</dd>
                  <dt>Import attempts</dt><dd>{{ displayCount(run.attempt) }}</dd>
                  <dt>Started</dt><dd>{{ displayDate(run.startedAt) }}</dd>
                </dl>
              </details>
            </td>
            <td class="text-end history-count">{{ displayCount(run.discovered) }}</td>
            <td class="text-end history-count">{{ displayCount(run.updated) }}</td>
            <td class="text-end history-count">{{ displayCount(run.created) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-else-if="loaded && !loading" class="text-muted">
      {{ nextCursor ? 'No imports for this municipality in the runs checked so far. Load older runs to continue.' : 'No recorded extract imports for this municipality.' }}
    </p>
    <p v-if="loading" class="text-muted" role="status">Loading import history…</p>
    <button v-if="nextCursor && !error" class="btn btn-sm btn-outline-primary" :disabled="loading" @click="loadHistory(nextCursor)">
      Load older runs
    </button>
    <p class="small text-muted mt-2 mb-0">Legacy listing discovery and failures before an extract run was recorded are not included.</p>
  </section>
</template>

<script>
import { fetchApi } from '@/api';

export default {
  props: {
    cityId: { type: String, required: true },
    cityName: { type: String, required: true },
    refreshKey: { type: String, default: null }
  },
  data() {
    return { imports: [], nextCursor: null, loading: false, loaded: false, error: '', failedAfter: null, requestId: 0 };
  },
  watch: {
    cityId: { immediate: true, handler() {
      this.imports = [];
      this.nextCursor = null;
      this.loaded = false;
      this.loadHistory();
    } },
    refreshKey() { this.loadHistory(); }
  },
  beforeUnmount() { this.requestId++; },
  methods: {
    async loadHistory(after = null) {
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = '';
      this.failedAfter = after;
      try {
        const query = after ? `?after=${encodeURIComponent(after)}` : '';
        const result = await fetchApi(`/v1/admin/listing-imports/${encodeURIComponent(this.cityId)}${query}`);
        if (requestId !== this.requestId) return;
        const rows = after ? [...this.imports, ...result.imports] : result.imports;
        this.imports = [...new Map(rows.map(run => [run.importId, run])).values()];
        this.nextCursor = result.nextCursor;
        this.loaded = true;
      } catch {
        if (requestId !== this.requestId) return;
        this.error = this.loaded
          ? 'Could not load import history. Showing the last loaded results.'
          : 'Could not load import history.';
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    displayDate(value) { return value ? new Date(value).toLocaleString() : '—'; },
    displayCount(value) { return Number.isInteger(value) ? value.toLocaleString() : '—'; },
    sourceNames(sources) {
      const names = { airbnb: 'Airbnb', vrbo: 'Vrbo', booking: 'Booking.com' };
      return sources.map(source => names[source] || source).join(', ');
    },
    statusLabel(status) {
      return { completed: 'Completed', failed: 'Failed', running: 'Running', pending: 'Pending', not_started: 'Not reached', skipped: 'Skipped' }[status] || 'Unknown';
    },
    absenceReason(reason) {
      return {
        disabled: 'Absence policy is disabled.', filtered_import: 'This import covers a filtered selection.',
        rejected_rows: 'Some rows were rejected or could not be assigned to a municipality.',
        not_full_snapshot: 'This extract is not a full snapshot.', missing_snapshot_date: 'Snapshot date is missing or invalid.',
        stale_snapshot: 'Snapshot date is too old or in the future.', coverage_not_configured: 'Full snapshot coverage is not configured.',
        coverage_or_date_changed: 'Coverage is disabled, or this snapshot day was already processed or superseded.',
        processing_failed: 'Absence processing failed. Retrying this extract resumes the check.'
      }[reason] || 'Absence check was not performed.';
    },
    statusClass(status) {
      return { completed: 'bg-success', failed: 'bg-danger', running: 'bg-primary' }[status] || 'bg-secondary';
    }
  }
};
</script>

<style scoped>
.extract-details { min-width: 16rem; max-width: 32rem; }
.history-count { font-variant-numeric: tabular-nums; }
</style>
